// client/src/pages/admin/AdminManageStore.js
// One place for the shop's look and details: logo, banners, theme colors,
// name, and the business details printed on invoices.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Alert, CircularProgress, TextField, Grid, Divider,
  FormControlLabel, Switch,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { API_BASE_URL, getImageUrl } from '../../utils/media';
import { useStore } from '../../context/StoreContext';
import AdminBannerList from './AdminBannerList';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const THEME_PRESETS = [
  { name: 'Sky Blue & Orange', primaryColor: '#0B5FA5', secondaryColor: '#F26A21' },
  { name: 'Racing Red & Blue', primaryColor: '#D32F2F', secondaryColor: '#2563EB' },
  { name: 'Teal & Orange', primaryColor: '#00897B', secondaryColor: '#E65100' },
  { name: 'Airfield Green', primaryColor: '#388E3C', secondaryColor: '#E65100' },
];

const COLOR_FIELDS = [
  { key: 'primaryColor', label: 'Primary', helperText: 'Buttons, links, and the store name.' },
  { key: 'secondaryColor', label: 'Secondary', helperText: 'Accents and highlights.' },
  { key: 'successColor', label: 'Success', helperText: 'Success messages and in-stock labels.' },
  { key: 'warningColor', label: 'Warning', helperText: 'Warnings and low-stock labels.' },
  { key: 'errorColor', label: 'Error', helperText: 'Errors and delete buttons.' },
];

const SectionHeading = ({ title, description }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" fontWeight={700}>{title}</Typography>
    {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
  </Box>
);

const ImageField = ({ label, helperText, file, existingUrl, onPick, onRemove, previewSx }) => {
  // Only recreated when the picked File changes, so typing elsewhere in the
  // form doesn't leak a new blob URL on every keystroke.
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);
  const previewSrc = objectUrl || (existingUrl ? getImageUrl(existingUrl) : null);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>{label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{helperText}</Typography>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover', mb: 1.5, ...previewSx,
        }}
      >
        {previewSrc ? (
          <Box component="img" src={previewSrc} alt={`${label} preview`} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        ) : (
          <Typography variant="caption" color="text.secondary">None yet</Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1}>
        <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
          {previewSrc ? 'Replace' : 'Upload'}
          <input
            hidden type="file" accept="image/*"
            onChange={(e) => { if (e.target.files[0]) onPick(e.target.files[0]); e.target.value = ''; }}
          />
        </Button>
        {previewSrc && <Button size="small" color="error" onClick={onRemove}>Remove</Button>}
      </Stack>
    </Box>
  );
};

const ColorOverrideField = ({ label, helperText, enabled, color, onToggle, onColorChange }) => (
  <Box>
    <FormControlLabel
      control={<Switch checked={enabled} onChange={(e) => onToggle(e.target.checked)} />}
      label={label}
    />
    <Typography variant="body2" color="text.secondary" sx={{ mb: enabled ? 1 : 0 }}>{helperText}</Typography>
    {enabled && (
      <TextField
        type="color" fullWidth size="small" aria-label={label}
        value={color}
        onChange={(e) => onColorChange(e.target.value)}
      />
    )}
  </Box>
);

const AdminManageStore = () => {
  const { refreshStore } = useStore();

  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/store`, { headers: authHeader() });
      if (!response.ok) throw new Error('Failed to load store settings.');
      const data = await response.json();
      setForm({
        name: data.name,
        supportEmail: data.support_email || '',
        address: data.address || '',
        vatNumber: data.vat_number || '',
        bankName: data.bank_name || '',
        bankAccountName: data.bank_account_name || '',
        bankAccountNumber: data.bank_account_number || '',
        bankBranchCode: data.bank_branch_code || '',
        bankAccountType: data.bank_account_type || '',
        paymentInstructions: data.payment_instructions || '',
        aboutText: data.about_text || '',
        phone: data.phone || '',
        whatsappNumber: data.whatsapp_number || '',
        tradingHours: data.trading_hours || '',
        primaryColor: data.primary_color,
        secondaryColor: data.secondary_color,
        successColor: data.success_color,
        warningColor: data.warning_color,
        errorColor: data.error_color,
        useBackgroundColor: Boolean(data.background_color),
        backgroundColor: data.background_color || '#F6F7FB',
        useTextColor: Boolean(data.text_color),
        textColor: data.text_color || '#1A1A1A',
        logoUrl: data.logo_url,
        bannerUrl: data.banner_url,
        logoFile: null,
        bannerFile: null,
      });
    } catch (err) {
      setLoadError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (changes) => {
    setForm((current) => ({ ...current, ...changes }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');
    setSaved(false);
    setSaving(true);
    try {
      const body = new FormData();
      ['name', 'supportEmail', 'address', 'vatNumber', 'bankName', 'bankAccountName', 'bankAccountNumber', 'bankBranchCode', 'bankAccountType', 'paymentInstructions', 'aboutText', 'phone', 'whatsappNumber', 'tradingHours', ...COLOR_FIELDS.map((f) => f.key)]
        .forEach((key) => body.append(key, form[key]));
      body.append('backgroundColor', form.useBackgroundColor ? form.backgroundColor : '');
      body.append('textColor', form.useTextColor ? form.textColor : '');
      if (form.logoFile) body.append('logo', form.logoFile);
      else if (!form.logoUrl) body.append('removeLogo', 'true');
      if (form.bannerFile) body.append('banner', form.bannerFile);
      else if (!form.bannerUrl) body.append('removeBanner', 'true');

      const response = await fetch(`${API_BASE_URL}/api/admin/store`, {
        method: 'PUT', headers: authHeader(), body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to save. Check that your images are under 25 MB.');

      await load();
      await refreshStore();
      setSaved(true);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!form) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Manage Store</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Your logo, banners, theme colors, and business details. Press Save Changes to put them live on your site.
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <SectionHeading title="Logo & Main Banner" />
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <ImageField
              label="Logo"
              helperText="Shown next to your store name and in the browser tab. A square image works best, at least 256 x 256 px."
              file={form.logoFile}
              existingUrl={form.logoUrl}
              onPick={(file) => update({ logoFile: file })}
              onRemove={() => update({ logoFile: null, logoUrl: null })}
              previewSx={{ width: 160, height: 160 }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <ImageField
              label="Main Banner"
              helperText="The first image at the top of your homepage. Use a wide image, about 1920 x 520 px. The edges may be cropped on small screens, so keep important content near the center."
              file={form.bannerFile}
              existingUrl={form.bannerUrl}
              onPick={(file) => update({ bannerFile: file })}
              onRemove={() => update({ bannerFile: null, bannerUrl: null })}
              previewSx={{ width: '100%', height: 160 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <SectionHeading
          title="Theme & Colors"
          description="Start from a theme, or pick your own colors below. Visitors can switch between light and dark mode, so avoid very dark or very pale colors or some text may become hard to read."
        />
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 3 }}>
          {THEME_PRESETS.map(({ name, primaryColor, secondaryColor }) => (
            <Button
              key={name}
              variant="outlined"
              color="inherit"
              onClick={() => update({ primaryColor, secondaryColor })}
              startIcon={(
                <Box sx={{ display: 'flex' }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: primaryColor }} />
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: secondaryColor, ml: -0.5 }} />
                </Box>
              )}
            >
              {name}
            </Button>
          ))}
        </Stack>
        <Grid container spacing={2}>
          {COLOR_FIELDS.map(({ key, label, helperText }) => (
            <Grid item xs={6} md={4} lg key={key}>
              <TextField
                label={label} type="color" fullWidth
                helperText={helperText}
                value={form[key]}
                onChange={(e) => update({ [key]: e.target.value })}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <ColorOverrideField
              label="Custom background color"
              helperText="Off: the background follows each visitor's light or dark mode. On: this color is always used."
              enabled={form.useBackgroundColor}
              color={form.backgroundColor}
              onToggle={(checked) => update({ useBackgroundColor: checked })}
              onColorChange={(color) => update({ backgroundColor: color })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ColorOverrideField
              label="Custom text color"
              helperText="Off: the text follows each visitor's light or dark mode. On: this color is always used."
              enabled={form.useTextColor}
              color={form.textColor}
              onToggle={(checked) => update({ useTextColor: checked })}
              onColorChange={(color) => update({ textColor: color })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <SectionHeading title="Store Details" />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Store Name" required fullWidth
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Support Email" type="email" fullWidth
              value={form.supportEmail}
              onChange={(e) => update({ supportEmail: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Physical Address" fullWidth multiline minRows={2}
              helperText="Printed on invoices, quotes, and proforma invoices."
              value={form.address}
              onChange={(e) => update({ address: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="VAT Number" fullWidth
              helperText="Leave empty if the business isn't VAT-registered."
              value={form.vatNumber}
              onChange={(e) => update({ vatNumber: e.target.value })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <SectionHeading
          title="About & Contact"
          description="Shown on your public About & Contact page, along with your support email and physical address above."
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="About your shop" fullWidth multiline minRows={4}
              helperText="Your story: who you are, what you love about the hobby, and why people should buy from you."
              value={form.aboutText} onChange={(e) => update({ aboutText: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Phone" fullWidth value={form.phone} onChange={(e) => update({ phone: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="WhatsApp number" fullWidth placeholder="e.g., 082 123 4567"
              helperText="Adds a chat button across the shop. Leave empty to hide it."
              value={form.whatsappNumber} onChange={(e) => update({ whatsappNumber: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Trading hours" fullWidth placeholder="e.g., Mon-Fri 8:00-17:00" value={form.tradingHours} onChange={(e) => update({ tradingHours: e.target.value })} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <SectionHeading
          title="Banking Details for EFT"
          description="Printed on every invoice and shown to customers after checkout, so they know where to pay."
        />
        {!(form.bankName && form.bankAccountNumber) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Add your bank name and account number. Until then, customers are told to contact you for your banking details.
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Bank" fullWidth value={form.bankName} onChange={(e) => update({ bankName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Account Name" fullWidth value={form.bankAccountName} onChange={(e) => update({ bankAccountName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Account Number" fullWidth value={form.bankAccountNumber} onChange={(e) => update({ bankAccountNumber: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Branch Code" fullWidth value={form.bankBranchCode} onChange={(e) => update({ bankBranchCode: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Account Type" fullWidth placeholder="e.g. Cheque" value={form.bankAccountType} onChange={(e) => update({ bankAccountType: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Payment Instructions (optional)" fullWidth multiline minRows={2}
              helperText="Replaces the default line asking customers to send proof of payment to your support email."
              value={form.paymentInstructions}
              onChange={(e) => update({ paymentInstructions: e.target.value })}
            />
          </Grid>
        </Grid>

        {saveError && <Alert severity="error" sx={{ mt: 3 }}>{saveError}</Alert>}
        {saved && <Alert severity="success" sx={{ mt: 3 }}>Changes saved.</Alert>}

        <Button type="submit" variant="contained" size="large" disabled={saving} sx={{ mt: 3 }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Outside the form above: this section saves each slide as it's changed,
          and its dialog's submit must not also trigger Save Changes. */}
      <AdminBannerList />
    </Box>
  );
};

export default AdminManageStore;
